/**
 * Review Settings — section card for the Storefront Settings page.
 *
 * Follows the same pattern as ProductDisplaySettings: toggle + optimistic update
 * via useStorefrontTenantSettings().
 */

import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { toast } from "sonner"
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings"
import type { TenantSettingsResponse } from "@/lib/api/controlPlaneClient"

function SettingRow({
  label,
  subtitle,
  children,
}: {
  label: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-4 py-5">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        {subtitle && (
          <span className="text-xs text-muted-foreground">{subtitle}</span>
        )}
      </div>
      {children}
    </div>
  )
}

function Divider() {
  return <div className="border-t border-border mx-4" />
}

export function ReviewSettings() {
  const {
    settings,
    isLoading,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()

  const [localMinRating, setLocalMinRating] = useState("4")
  const [localReminderDays, setLocalReminderDays] = useState("7")

  useEffect(() => {
    if (settings) {
      setLocalMinRating(String(settings.review_auto_approve_min_rating ?? 4))
      setLocalReminderDays(String(settings.review_reminder_days ?? 7))
    }
  }, [settings])

  const handleToggle = async (
    field: keyof TenantSettingsResponse,
    checked: boolean,
  ) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )
    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, [field]: checked ? 1 : 0 } : old),
    )
    try {
      await updateAsync({ [field]: checked ? 1 : 0 })
      toast("Setting saved.")
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error("Failed to save. Please try again.")
    }
  }

  const handleSaveRatingStyle = async (style: string) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )
    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, review_rating_style: style } : old),
    )
    try {
      await updateAsync({ review_rating_style: style })
      toast("Setting saved.")
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error("Failed to save.")
    }
  }

  const handleSaveMinRating = async () => {
    const val = Math.min(5, Math.max(1, parseInt(localMinRating, 10) || 4))
    setLocalMinRating(String(val))
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )
    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old ? { ...old, review_auto_approve_min_rating: val } : old,
    )
    try {
      await updateAsync({ review_auto_approve_min_rating: val })
      toast("Setting saved.")
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error("Failed to save. Please try again.")
    }
  }

  const handleSaveReminderDays = async () => {
    const val = Math.max(1, parseInt(localReminderDays, 10) || 7)
    setLocalReminderDays(String(val))
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )
    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, review_reminder_days: val } : old),
    )
    try {
      await updateAsync({ review_reminder_days: val })
      toast("Setting saved.")
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error("Failed to save. Please try again.")
    }
  }

  const enableReviews = !!settings?.enable_reviews
  const autoApprove = !!settings?.review_auto_approve
  const reminderEnabled = !!settings?.review_reminder_enabled
  const ratingStyle = settings?.review_rating_style ?? "Stars"

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        {/* Enable Reviews */}
        <SettingRow
          label="Enable Reviews"
          subtitle="Allow customers to leave product reviews"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={enableReviews}
              onCheckedChange={(c) => handleToggle("enable_reviews", c)}
              disabled={isUpdating}
              aria-label="Enable product reviews"
            />
          )}
        </SettingRow>

        {enableReviews && (
          <>
            <Divider />

            {/* Auto-approve */}
            <SettingRow
              label="Auto-approve Reviews"
              subtitle="Automatically approve reviews meeting minimum rating"
            >
              {isLoading ? (
                <Skeleton className="w-9 h-5 rounded-full" />
              ) : (
                <Switch
                  checked={autoApprove}
                  onCheckedChange={(c) =>
                    handleToggle("review_auto_approve", c)
                  }
                  disabled={isUpdating}
                  aria-label="Auto-approve reviews"
                />
              )}
            </SettingRow>

            {autoApprove && (
              <>
                <Divider />
                <SettingRow
                  label="Min Rating for Auto-approve"
                  subtitle="Reviews with this rating or higher are auto-approved"
                >
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={localMinRating}
                    onChange={(e) => setLocalMinRating(e.target.value)}
                    onBlur={handleSaveMinRating}
                    className="w-16 h-8 text-center text-sm"
                    disabled={isUpdating}
                    aria-label="Minimum rating for auto-approve"
                  />
                </SettingRow>
              </>
            )}

            <Divider />

            {/* Rating style */}
            <SettingRow
              label="Rating Style"
              subtitle="Choose how ratings are displayed"
            >
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="rating_style"
                    checked={ratingStyle === "Stars"}
                    onChange={() => handleSaveRatingStyle("Stars")}
                    className="accent-primary"
                    disabled={isUpdating}
                  />
                  Stars
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="rating_style"
                    checked={ratingStyle === "Hearts"}
                    onChange={() => handleSaveRatingStyle("Hearts")}
                    className="accent-primary"
                    disabled={isUpdating}
                  />
                  Hearts
                </label>
              </div>
            </SettingRow>

            <Divider />

            {/* Review reminder */}
            <SettingRow
              label="Review Reminder"
              subtitle="Send reminder after order delivery"
            >
              {isLoading ? (
                <Skeleton className="w-9 h-5 rounded-full" />
              ) : (
                <Switch
                  checked={reminderEnabled}
                  onCheckedChange={(c) =>
                    handleToggle("review_reminder_enabled", c)
                  }
                  disabled={isUpdating}
                  aria-label="Enable review reminders"
                />
              )}
            </SettingRow>

            {reminderEnabled && (
              <>
                <Divider />
                <SettingRow
                  label="Days After Delivery"
                  subtitle="Send reminder this many days after delivery"
                >
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={localReminderDays}
                    onChange={(e) => setLocalReminderDays(e.target.value)}
                    onBlur={handleSaveReminderDays}
                    className="w-16 h-8 text-center text-sm"
                    disabled={isUpdating}
                    aria-label="Days after delivery for review reminder"
                  />
                </SettingRow>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
